// =====================================================
// 📁 QuotationHistoryCard.tsx
// =====================================================

import React from 'react';

import { CircleX, Eye, FileText, Pencil, ReceiptText } from 'lucide-react';

import { useQuotation } from '../../../../../hooks/sale/useQuotation';
import useQuotationPdf from '../../../../../hooks/pdf/useQuotationPdf';

import { formatCurrency } from '../../../../../utils/formatCurrency';

import ActionButton from '../../../../Buttons/ActionButton';
import Can from '../../../../auth/Can';

interface Props {
  quotation: any;
}

const QuotationHistoryCard: React.FC<Props> = ({ quotation }) => {
  // =====================================================
  // 🔹 Hooks
  // =====================================================

  const {
    cancelActive,
    findById,
    findDetailById,
    handlerSelectQuotation,
    openProcessModal,
  } = useQuotation();

  const { handleDownloadQuotationPdf } = useQuotationPdf();

  // =====================================================
  // 🔹 Actions
  // =====================================================

  const handleProcess = () => {
    handlerSelectQuotation(quotation);
    openProcessModal();
  };

  const handleCancel = async () => {
    await cancelActive(quotation.id);
  };

  const handleEdit = async () => {
    await findById(quotation.id);
  };

  const handleView = async () => {
    await findDetailById(quotation.id);
  };

  const handlePdf = () => {
    handleDownloadQuotationPdf(quotation.id);
  };

  // =====================================================
  // 🔹 Values
  // =====================================================

  const isExpired = quotation.status === 'EXPIRED';

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <div
      className="
        overflow-hidden

        rounded-xl

        border
        border-stroke

        bg-white

        shadow-1

        transition-all
        duration-200

        hover:border-primary/20

        dark:border-strokedark
        dark:bg-boxdark
      "
    >
      {/* ===================================================== */}
      {/* Header */}
      {/* ===================================================== */}

      <div
        className="
          flex
          flex-col
          gap-4

          p-4

          dark:bg-black

          sm:flex-row
          sm:items-center
          sm:justify-between
        "
      >
        {/* ===================================================== */}
        {/* Left */}
        {/* ===================================================== */}

        <div
          className="
            flex
            min-w-0
            flex-1
            items-start
            gap-3

            sm:items-center
          "
        >
          <div
            className="
              flex
              h-10
              w-10
              shrink-0
              items-center
              justify-center

              rounded-xl

              bg-primary/10

              text-primary
            "
          >
            <FileText size={18} />
          </div>

          <div className="min-w-0 flex-1">
            <div
              className="
                flex
                flex-wrap
                items-center
                gap-2
              "
            >
              <h4
                className="
                  truncate

                  text-sm
                  font-bold

                  text-primary
                "
              >
                {quotation.quotationNumber}
              </h4>

              <span
                className={`
                  rounded-full

                  px-2
                  py-0.5

                  text-[10px]
                  font-semibold

                  ${
                    isExpired
                      ? 'bg-danger/10 text-danger'
                      : 'bg-success/10 text-success'
                  }
                `}
              >
                {isExpired ? 'Expirada' : 'Activa'}
              </span>
            </div>

            <p
              className="
                mt-1

                break-words

                text-sm
                font-semibold

                text-black

                dark:text-white
              "
            >
              {quotation.customerName}
            </p>
          </div>
        </div>

        {/* ===================================================== */}
        {/* Right */}
        {/* ===================================================== */}

        <div
          className="
            flex
            items-end
            justify-between

            border-t
            border-stroke

            pt-3

            dark:border-strokedark

            sm:ml-4
            sm:block
            sm:shrink-0
            sm:border-0
            sm:pt-0
            sm:text-right
          "
        >
          <div>
            <p
              className="
                text-[10px]
                uppercase
                tracking-wide

                text-body
              "
            >
              Total
            </p>

            <p
              className="
                text-lg
                font-bold

                leading-none

                text-primary

                sm:text-title-xsm
              "
            >
              ${formatCurrency(quotation.total)}
            </p>
          </div>

          <p
            className="
              text-xs
              font-medium

              text-body

              sm:mt-1
            "
          >
            {quotation.expirationDate}
          </p>
        </div>
      </div>

      {/* ===================================================== */}
      {/* Actions */}
      {/* ===================================================== */}

      <div
        className="
          flex
          flex-col
          gap-2

          border-t
          border-stroke

          bg-gray-2

          p-3

          dark:border-strokedark
          dark:bg-boxdark-2

          sm:flex-row
          sm:flex-wrap
          sm:justify-end
        "
      >
        <Can perm="QUOTATIONS_PROCESS">
          <div className="w-full sm:w-auto">
            <ActionButton
              label="Procesar"
              icon={<ReceiptText size={14} />}
              variant="success"
              fullWidth
              onClick={handleProcess}
            />
          </div>
        </Can>

        <Can anyOf={['QUOTATIONS_VIEW', 'QUOTATIONS_EXPORT']}>
          <div className="w-full sm:w-auto">
            <ActionButton
              label="PDF"
              icon={<FileText size={14} />}
              variant="dark"
              fullWidth
              onClick={handlePdf}
            />
          </div>
        </Can>

        <Can perm="QUOTATIONS_EDIT">
          <div className="w-full sm:w-auto">
            <ActionButton
              label="Editar"
              icon={<Pencil size={14} />}
              variant="primary"
              fullWidth
              onClick={handleEdit}
            />
          </div>
        </Can>

        <Can perm="QUOTATIONS_VIEW">
          <div className="w-full sm:w-auto">
            <ActionButton
              label="Ver"
              icon={<Eye size={14} />}
              variant="info"
              fullWidth
              onClick={handleView}
            />
          </div>
        </Can>

        <Can perm="QUOTATIONS_CANCEL">
          <div className="w-full sm:w-auto">
            <ActionButton
              label="Cancelar"
              icon={<CircleX size={14} />}
              variant="danger"
              fullWidth
              onClick={handleCancel}
            />
          </div>
        </Can>
      </div>
    </div>
  );
};

export default QuotationHistoryCard;
